import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  Card,
  CardContent,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import axios from "axios";
import Fuse from "fuse.js";
import stringSimilarity from "string-similarity";

const cellStyle = {
  padding: "20px",
  textAlign: "center",
  fontWeight: "bold",
  fontSize: "1.2rem",
  wordBreak: "break-word",
  whiteSpace: "normal",
  overflowWrap: "break-word",
};

export const TabThree = ({ onSearchResultsFound }) => {
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [allTaxData, setAllTaxData] = useState([]);
  const [initialDataLoading, setInitialDataLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [open, setopen] = useState(false);

  const isDesktop = useMediaQuery("(min-width:768px)");


  useEffect(() => {
    // Example: fetch or set data here
    // After fetching:
    if (!loading && filteredData.length > 0) {
      onSearchResultsFound(true); // tell parent to hide header
    } else {
      onSearchResultsFound(false); // show header again
    }
  }, [loading, filteredData]);

  useEffect(() => {
    const fetchAllTaxData = async () => {
      setInitialDataLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/get-tax-data`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const apiData = result.data || [];
        const formattedData = apiData.map((item, index) => ({
          id: index,
          label: `${item["Company Name"] || ""} (${item.Year || ""})`.trim(),
          value: item["Company Name"] || "",
          companyName: item["Company Name"] || "",
          year: item.Year || "",
          taxesPaid: item["Taxes Paid"] || item["Tax Paid"] || "N/A",
          taxesAvoided: item["Taxes Avoided"] || item["Tax Avoided"] || "N/A",
          searchText: [
            (item["Company Name"] || "").toLowerCase(),
            (item.Year || "").toString().toLowerCase(),
            `${item["Company Name"] || ""} ${item.Year || ""}`.toLowerCase(),
          ].filter((text) => text.trim() !== ""),
        }));

        setAllTaxData(formattedData);
      } catch (err) {
        console.error("Failed to fetch initial tax data:", err);
        setError("Failed to load tax data. Please refresh the page.");
      } finally {
        setInitialDataLoading(false);
      }
    };

    fetchAllTaxData();
  }, []);
  const suggestions = useMemo(() => {
    if (!searchQuery?.trim() || allTaxData.length === 0) return [];

    const query = searchQuery.toLowerCase().trim();

    // ✅ Capture company name parts including special chars
    const queryParts = Array.from(
      new Set(query.match(/[a-z0-9&@\-./']+/gi) || [])
    );

    // Extract year from query
    const queryYearMatch = query.match(/\d{4}/);
    const queryYear = queryYearMatch ? parseInt(queryYearMatch[0], 10) : null;

    // Remove the year from queryParts for company matching
    const companyParts = queryParts.filter(
      (p) => p !== (queryYear?.toString() ?? "")
    );

    // Levenshtein distance function for fuzzy matching
    const levenshtein = (a, b) => {
      const dp = Array.from({ length: a.length + 1 }, (_, i) =>
        Array(b.length + 1).fill(0)
      );
      for (let i = 0; i <= a.length; i++) dp[i][0] = i;
      for (let j = 0; j <= b.length; j++) dp[0][j] = j;
      for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
          dp[i][j] =
            a[i - 1] === b[j - 1]
              ? dp[i - 1][j - 1]
              : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
        }
      }
      return dp[a.length][b.length];
    };

    const results = allTaxData
      .filter((item) => {
        // strict year match if query includes a year
        if (queryYear && item.year !== queryYear) return false;

        // at least one part of company name must match
        if (companyParts.length > 0) {
          const company = item.companyName.toLowerCase();
          const matches = companyParts.some((p) => company.includes(p));
          if (!matches) return false;
        }

        return true;
      })
      .map((item) => {
        const company = item.companyName.toLowerCase();
        const year = item.year;
        let score = 1;

        // exact / startsWith boosts
        const companyJoined = companyParts.join(" ");
        if (company === companyJoined) score -= 0.6;
        else if (company.startsWith(companyJoined)) score -= 0.4;
        if (year === queryYear) score -= 0.5;

        // fuzzy company matching
        const companyDist = levenshtein(companyJoined, company);
        score += companyDist * 0.05;

        return { ...item, score };
      })
      // ✅ Sort by year descending first, then by score ascending
      .sort((a, b) => {
        if (b.year !== a.year) return b.year - a.year;
        return a.score - b.score;
      })
      .slice(0, 50);

    return results;
  }, [searchQuery, allTaxData]);

  const handleSearch = async (query) => {
    setSearchQuery(query);

    if (!query.trim()) {
      setFilteredData([]);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/tax-semantic-search`,
        {
          query: query,
          tab_type: "tax",
        }
      );

      const rawRows = res.data?.data || [];

      const rows = rawRows.map((row, index) => ({
        id: `${(row["Company Name"] || "").trim()}-${(row["Year"] || "")
          .toString()
          .trim()}-${index}`,
        company_name: (row["Company Name"] || "").trim(),
        year: (row["Year"] || "").toString().trim(),
        tax_paid: (row["Taxes Paid"] || "").trim(),
        tax_avoid: (row["Taxes Avoided"] || "").trim(),
      }));

      // ✅ Split query into words + numbers (so Apple2024 → ["apple", "2024"])
      const searchTerms =
        query
          .toLowerCase()
          .trim()
          .match(/[a-z]+|\d+/g) || [];

      const filtered = rows.filter((row) => {
        const companyName = (row.company_name || "").toLowerCase();
        const year = (row.year || "").toString().toLowerCase();

        return searchTerms.some(
          (term) => companyName.includes(term) || year.includes(term)
        );
      });

      const sorted = [...filtered]
        .filter((row) => row.year)
        .sort((a, b) => parseInt(b.year) - parseInt(a.year));

      const topFourYears = sorted.slice(0, 4);

      setFilteredData(topFourYears);
      setError("");
    } catch (err) {
      setFilteredData([]);
      console.error("Tax search failed:", err);
      setError(`Tax data Not Matched with: ${query}.`);

      // setError(`Failed to load data: ${err.message}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch(searchQuery);
    }
  };

  const handleSuggestionSelect = (event, value) => {
    if (value && typeof value === "object") {
      const payload = {
        companyName: value.companyName,
        year: value.year,
        taxesPaid: value.taxesPaid,
        taxesAvoided: value.taxesAvoided,
      };
      setSelectedItem(payload);
      setSearchQuery(value.label);
      const searchTerm = `${value.companyName} (${value.year})`;
      handleSearch(searchTerm);
    } else if (typeof value === "string") {
      const isNumeric = (value) => /^\d+$/.test(value);
      if (isNumeric(value.trim())) {
        if (suggestions.length >= 1) {
          handleSearch(suggestions[0].label);
          setSearchQuery(suggestions[0].label);
        } else handleSearch(value.trim());
      } else {
        const cleanValue = value.replace(/\\/g, "").trim();
        setSearchQuery(cleanValue);
        handleSearch(cleanValue);
      }
    }
  };

  const LoadingComponent = () => (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      py={8}
    >
      <CircularProgress size={60} thickness={4} />
      <Typography variant="h6" sx={{ mt: 2, color: "text.secondary" }}>
        Searching tax data...
      </Typography>
      <Typography variant="body2" sx={{ mt: 1, color: "text.disabled" }}>
        Analyzing company tax information
      </Typography>
    </Box>
  );

  const InitialLoadingComponent = () => (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      py={8}
    >
      <CircularProgress size={60} thickness={4} />
      <Typography variant="h6" sx={{ mt: 2, color: "text.secondary" }}>
        Loading tax data
      </Typography>
      <Typography variant="body2" sx={{ mt: 1, color: "text.disabled" }}>
        This may take a moment
      </Typography>
    </Box>
  );

  if (initialDataLoading) {
    return <InitialLoadingComponent />;
  }

  return (
    <>
      <div className="meow">
        {
          filteredData.length > 0 ?
            <>
              <div className="pointer d-flex" onClick={() => {
                onSearchResultsFound(false)
                setError("");
                setSelectedOption(null);
                setSearchQuery("");
                setFilteredData([]);
                return;
                return;
              }}><img width="22" height="22" src="https://img.icons8.com/sf-ultralight-filled/25/00c062/chevron-left.png" alt="chevron-left" />
                <p className="mb-0 ms-1 back">Back to Home</p></div>
              <Typography variant="h4" textAlign="center" fontWeight="bold" className="py-gp mb-2"> Tax Avoidance</Typography>
            </> : ""

        }
        <Box m={0}
        //  className="nomargin"
         className={filteredData.length > 0 ? "nomargin texter" : "nomargin "}
        >
          <label>Search by company name or year</label>
          <Autocomplete
            freeSolo
            value={selectedOption}
            options={suggestions}
            // className="autoinput"
             className={filteredData.length > 0 ? "autoinput2 mt-2" : "autoinput mt-2"}
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              return option.label || "";
            }}
            inputValue={searchQuery}
            onBlur={(event) => {
              const value = searchQuery.trim();
              const isNumeric = (value) => /^\d+$/.test(value);
              if (isNumeric(searchQuery)) {
                if (suggestions.length >= 1) {
                  handleSearch(suggestions[0].label);
                  setSearchQuery(suggestions[0].label);
                } else handleSearch(searchQuery.trim());
              } else {
                handleSearch(searchQuery.trim());
              }
            }}
            onInputChange={(event, newInputValue) => {
              if (!newInputValue) {
                setError("");
                setSearchQuery("");
                setSelectedOption(null);
                setFilteredData([]);
                return;
              }
              setError("");
              setSelectedOption(null);
              setSearchQuery(newInputValue);
            }}
            onChange={handleSuggestionSelect}
            // onKeyDown={handleKeyPress}
            noOptionsText={
              searchQuery.length < 1
                ? "Start typing to search for companies or years..."
                : "No matching tax data found"
            }
            disabled={loading}
            filterOptions={(options) => options}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Box
                  sx={{ display: "flex", alignItems: "center", width: "100%" }}
                >
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      <span style={{ color: "#1976d2" }}>
                        {option.companyName}
                      </span>
                      <span style={{ color: "#999", fontWeight: "normal" }}>
                        {" "}
                        ({option.year})
                      </span>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Paid: {option.taxesPaid} • Avoided: {option.taxesAvoided}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                // label="Search by company name or year"
                variant="outlined"
                className="input-form"
                placeholder="Type to filter..."
                fullWidth
                disabled={loading || initialDataLoading}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && <CircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      </div>

      {loading && <LoadingComponent />}

      {error && (
        <Typography color="error" align="center">
          {error}
        </Typography>
      )}

      {/* <>
      {!loading && searchQuery && filteredData.length === 0 && !error && (
        <Typography
        align="center"
        color="textSecondary"
        my={4}
          dangerouslySetInnerHTML={{
            __html: `No Match found for <strong>${searchQuery
              .split(" ")
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(" ")}</strong>`,
            }}
            />
          )}
          </> */}

      {!!filteredData.length && !error && (
        <div className="taxavoidance_card">
          {/* Body */}
          {filteredData.map((row, index) => (
            <>
              <Card
                elevation={3}
                sx={{
                
                  p: 2,
                  borderRadius: 3,
                  mb: 2,
                  boxShadow:"none"
                }}
                 className="pay-gap"
              >
                {/* Left Section */}
                <CardContent
                  className="nopadding"
                  sx={{
                    // width: "50%",
                    marginRight: "0px",
                    flex: 1,
                    p: 0,
                  }}
                >
                  <Typography
                    variant="h5"
                    align="left"
                    sx={{ fontWeight: "600", mb: 1 , fontSize: "24px" }}
                  >
                    {row.company_name} ({row.year})
                    {!isDesktop ? (
                      <Tooltip
                        title="an estimate"
                        placement="top"
                        open={open}
                        sx={{ cursor: "pointer" }}
                      >
                        <span onClick={() => setopen(!open)}>*</span>
                      </Tooltip>
                    ) : (
                      <Tooltip
                        title="an estimate"
                        placement="top"
                        sx={{ cursor: "pointer" }}
                      >
                        *
                      </Tooltip>
                    )}
                  </Typography>
                  <Box
                    key={`${row.company_name}-${row.year}-${index}`}
                    className="tablebody"
                    sx={{
                      display: "flex",
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "#14FF9580",
                        color: "#04512F",
                        width: "50%",
                        marginRight:"12px",
                        borderRadius:"14px"
                        // borderRight: "1px solid gray",
                      }}
                    >
                      <Typography variant="h6">
                        <span className="border-b-4 text-tax border-[#0C7E57] ">
                          Tax Paid
                        </span>
                        <span className="TaxValue ">{row.year == 2025 ? row.tax_paid :  row.tax_paid =="N/A" ? "$0.00" : row.tax_paid }</span>
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: "#FF7D7D80",
                        color: "#6F0808",
                        width: "50%",
                         borderRadius:"14px"
                      }}
                    >
                      <Typography variant="h6">
                        <span className="border-b-4 text-tax border-[#EC4137] ">
                          Tax Avoided
                        </span>
                        <span className="TaxValue">{row.tax_avoid}</span>
                      </Typography>
                    </Box>
                  </Box>
                   <Typography variant="h6" mt="10px" align="left" fontWeight="600" className="pro-heading border-t border-gray-400">
                      Summary
                    </Typography>
                  <Typography variant="h6" mt="10px" align="left" fontSize="18px" fontWeight="400" className="copy_right">
                    {row.company_name} paid {row.tax_paid} but avoided an
                    estimate of {row.tax_avoid} in taxes in {row.year} by
                    exploiting tax loopholes.
                  </Typography>
                </CardContent>
              </Card>
            </>
          ))}
        </div>
      )}
    </>
  );
};